/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Fragment } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { RatingGaugeItem } from './RatingGaugeItem';
import { useEvents } from './useEvents';
import { classNames } from '../utils/UNSAFE_classNames';
import { getDimensions } from './RatingUtils';
import { useTooltipOrDatatip } from './useTooltipOrDatatip';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { Threshold, CommitDetail, InputDetail } from '../utils/UNSAFE_meterTypes';
import {
  getThresholdColorFromValue,
  getMeterAriaProps,
  validateRange
} from '../utils/PRIVATE_meterUtils';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { RatingGaugeStyles, RatingGaugeVariantOptions } from './themes/RatingGaugeStyles.css';
import { RatingGaugeRedwoodTheme } from './themes/redwood/RatingGaugeTheme';

/**
 * Props for the RatingGauge
 */
type Props = {
  /**
   * Defines whether the rating gauge should be read only. User interaction is prevented if set to true.
   */
  isReadonly?: boolean;

  /**
   * Defines whether the gauge is disabled or not. User interaction is prevented and the rating gauge is hidden from screen readers if set to true.
   */
  isDisabled?: boolean;

  /**
   * The size of the individual rating gauge items.
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * The color of the individual rating gauge items.
   */
  color?: 'neutral' | 'gold';

  /**
   * The value of the rating gauge.
   */
  value?: number;

  /**
   * Specifies the increment by which values can be changed.
   */
  step?: number;

  /**
   * Integer value specifying the maximum value of the gauge, which determines the number of items that are displayed
   */
  max?: number;

  /**
   * The callback called in response to hover or focus interactions with the rating gauge items.
   */
  onInput?: (detail: InputDetail) => void;

  /**
   * The callback called when value of gauge changes on blur or Enter or click.
   */
  onCommit?: (detail: CommitDetail) => void;

  /**
   * The rating gauge tooltip. Tooltip is only used for read only rating gauges. If aria-label is not specified, tooltip will be used as the aria-label.
   */
  tooltip?: string;

  /**
   * The rating gauge datatip string. Datatip is used for interactive rating gauges.
   */
  datatip?: string;

  /**
   * A label to be used for accessibility purposes. Default label will be used if not provided. This will be used for the aria-label of the DOM element.
   */
  'aria-label'?: string;

  /**
   * One or more ids (separated by spaces) of elements that label the rating gauge. This will be used for the aria-labelledby of the DOM element.
   */
  'aria-labelledby'?: string;

  /**
   * One or more ids (separated by spaces) of elements that describe the rating gauge. This will be used for the aria-describedby of the DOM element.
   */
  'aria-describedby'?: string;

  /**
   * An array of Threshold objects.
   * Applications are required to include information about thresholds for screen readers to make their component accessible by using aria-describedby, aria-labelledby or aria-label.
   */
  thresholds?: Threshold[];
};

/**
 * A rating gauge displays information graphically, typically displaying or accepting user feedback on a product or service.
 */
export function RatingGauge({
  max = 5,
  value = 0,
  size,
  color = 'neutral',
  step = 1,
  isReadonly,
  isDisabled,
  ...props
}: Props) {
  validateRange(0, max, value, step);
  const rootRef = useRef<HTMLDivElement>(null);
  const dimensionsRef = useRef(getDimensions(null));

  const isInteractive = !isReadonly && !isDisabled;

  // Since the dimension are compute lazily after render, store width and x in ref
  // This is used to identify hovered and clicked star.
  // width of the gauge depends on size and max so passing them as dependencies for useEffect
  useEffect(() => {
    dimensionsRef.current = getDimensions(rootRef.current);
  }, [size, max]);

  const eventProps = useEvents(
    max,
    step,
    value,
    rootRef,
    dimensionsRef,
    isInteractive,
    props.onCommit,
    props.onInput
  );

  const { tooltipContent, tooltipProps } = useTooltipOrDatatip({
    max,
    value,
    isReadonly,
    isDisabled,
    tooltip: props.tooltip,
    datatip: props.datatip,
    thresholds: props.thresholds,
    width: dimensionsRef.current.width,
    ariaDescribedBy: props['aria-describedby']
  });

  const ariaProps = getMeterAriaProps(
    value,
    0,
    max,
    props['aria-label'],
    props['aria-labelledby'],
    props.thresholds,
    isDisabled,
    isReadonly,
    props.tooltip
  );

  const mergedEventProps = mergeProps(eventProps, tooltipProps);

  const itemColor = getThresholdColorFromValue(value, color, props.thresholds);

  const { isTabbable } = useTabbableMode();
  const {
    classes,
    styles: { baseStyle, interactiveStyle, itemStyle, sizeStyle }
  } = useComponentTheme<RatingGaugeVariantOptions, RatingGaugeStyles>(RatingGaugeRedwoodTheme, {
    size
  });

  return (
    <Fragment>
      <div
        {...ariaProps}
        ref={rootRef}
        class={classNames([classes, baseStyle, isInteractive ? interactiveStyle : undefined])}
        tabIndex={isTabbable && !isDisabled ? 0 : -1}
        {...mergedEventProps}>
        {[...Array(max)].map((_, index: number) => {
          const fillRatio = Math.min(Math.max(0, value - index), 1);
          return (
            <div className={classNames([sizeStyle, itemStyle])}>
              <RatingGaugeItem
                fillRatio={fillRatio}
                isDisabled={isDisabled}
                isReadonly={isReadonly}
                color={itemColor}
              />
            </div>
          );
        })}
      </div>
      {tooltipContent}
    </Fragment>
  );
}
